import React, { useState } from 'react';

import { Container, FeedbackContainer, LeftContainer } from './Contact.styled';
import { Phone, MedicalTruck, Comment } from 'assets/icons/Icons';
import { useContactInfo } from 'hooks/useContactInfo';
import { useOnWindowResize } from 'hooks/useOnWindowResize';
import theme from 'kb-shared/theme';
import breakpointsRaw from 'kb-shared/theme/breakpointsRaw';
import { pageUrl, RoutesPath } from 'utilities/pageUrl';
import { useBreakpoints } from 'utilities/useBreakpoints';

import { Feedback } from './Feedback';
import { InformationContainer } from './InformationContainer';
import { InformationContainerWithTooltip } from './InformationContainerWithTooltip';

export const Contact = ({ isLoggedIn }: { isLoggedIn: boolean }) => {
  const [textSize, setTextSize] = useState<'sm' | 'md'>('sm');
  const { numberLabel, number } = useContactInfo();
  const { isMobile } = useBreakpoints();

  useOnWindowResize(() => {
    const isSmallOrMedium = document.body.clientWidth <= breakpointsRaw.lg;

    setTextSize(isSmallOrMedium ? 'sm' : 'md');
  }, 300);

  const isSoleraLandingPage = () => location.pathname === RoutesPath.SOLERA_LANDING;

  const numberToDisplay = isSoleraLandingPage() ? '+01 (888) 532-4943' : number;
  const numberLabelToDisplay = isSoleraLandingPage() ? '888-532-4943' : numberLabel;

  return !isMobile ? (
    <Container>
      <LeftContainer>
        <InformationContainerWithTooltip
          tooltip={SEND_US_MESSAGE}
          minWidth="324px"
          href={pageUrl.messages()}
          icon={<Comment ariaHidden />}
          linkText={MESSAGE_US}
          textSize={textSize}
          linkColor={theme.colors.neutral.primaryNavy}
        />

        {numberLabelToDisplay && (
          <InformationContainerWithTooltip
            tooltip={CALL_US}
            minWidth="240px"
            href={`tel:${numberToDisplay}`}
            icon={<Phone ariaHidden />}
            text={CALL}
            textColor={theme.colors.neutral.primaryNavy}
            textSize={textSize}
            linkText={numberLabelToDisplay}
            linkColor={theme.colors.neutral.primaryNavy}
          />
        )}

        <InformationContainerWithTooltip
          tooltip={CALL_911}
          minWidth="378px"
          href={`tel:911`}
          icon={<MedicalTruck ariaHidden />}
          iconColor={theme.colors.semantic.darkRed}
          text={EMERGENCY}
          textColor={theme.colors.semantic.darkRed}
          textSize={textSize}
          linkText="Call 911"
          linkColor={theme.colors.semantic.darkRed}
        />
      </LeftContainer>
      <FeedbackContainer>{isLoggedIn && <Feedback textSize={textSize} />}</FeedbackContainer>
    </Container>
  ) : (
    <Container>
      <InformationContainer
        icon={<Comment ariaHidden />}
        href={pageUrl.messages()}
        linkText={MESSAGE_US}
        description={SEND_US_MESSAGE}
        size={textSize}
        linkColor={theme.colors.neutral.primaryNavy}
      />

      {numberLabelToDisplay && (
        <InformationContainer
          icon={<Phone ariaHidden />}
          href={`tel:${numberToDisplay}`}
          linkTextPrefix={CALL}
          linkText={numberLabelToDisplay}
          description={CALL_US}
          size={textSize}
          linkColor={theme.colors.neutral.primaryNavy}
        />
      )}

      <InformationContainer
        icon={<MedicalTruck ariaHidden />}
        iconColor={theme.colors.semantic.darkRed}
        href={`tel:911`}
        linkTextPrefix={EMERGENCY}
        linkText="Call 911"
        description={CALL_911}
        size={textSize}
        linkColor={theme.colors.semantic.darkRed}
      />
      <FeedbackContainer>{isLoggedIn && <Feedback textSize={textSize} />}</FeedbackContainer>
    </Container>
  );
};

const SEND_US_MESSAGE = 'Send us a message right through your portal — we’ll be in touch soon.';
const CALL_US = 'Call us for general questions or support during business hours.';
const CALL_911 =
  'If you’re experiencing a medical emergency, please call 911 or go to the nearest emergency room.';
const MESSAGE_US = 'Message Us!';
const CALL = 'Call ';
const EMERGENCY = 'Emergency? ';
